import moment from "moment";

const MONTHS = 12;

// --- SSA percentage constants ---
const EARLY_FIRST36_MONTH_PCT_PER_MONTH = 5 / 9 / 100; // 0.005555...
const EARLY_AFTER36_MONTH_PCT_PER_MONTH = 5 / 12 / 100; // 0.004166...
const DELAYED_CREDIT_PCT_PER_MONTH = 2 / 3 / 100; // 0.006666...

/**
 * Determine Full Retirement Age (FRA) based on birth year
 */
function getFRA(birthYear) {
  // Simplified: FRA is 67 for those born in 1960 or later, as per document
  // Add more conditions if needed for earlier birth years
  return birthYear >= 1960 ? 67 : 66; // Adjust based on SSA rules if needed
}

/**
 * Calculate monthly benefit from PIA using SSA reduction/credit rules
 */
export function calculateMonthlyBenefitFromPIA(pia, claimAge, fraAge) {
  const monthDiff = Math.round((claimAge - fraAge) * MONTHS); // + = after FRA, - = before FRA

  if (monthDiff < 0) {
    // Early claiming
    const monthsEarly = -monthDiff;
    const first36 = Math.min(36, monthsEarly);
    const after36 = Math.max(0, monthsEarly - 36);

    const reductionPct =
      first36 * EARLY_FIRST36_MONTH_PCT_PER_MONTH +
      after36 * EARLY_AFTER36_MONTH_PCT_PER_MONTH;

    const monthlyBenefit = pia * Math.max(0, 1 - reductionPct);
    return Math.round(monthlyBenefit * 100) / 100;
  } else if (monthDiff > 0) {
    // Delayed credits
    const creditPct = monthDiff * DELAYED_CREDIT_PCT_PER_MONTH;
    const monthlyBenefit = pia * (1 + creditPct);
    return Math.round(monthlyBenefit * 100) / 100;
  } else {
    return Math.round(pia * 100) / 100;
  }
}

/**
 * Calculate total lifetime benefit
 */
export const calculateLifetimeBenefit = (
  monthlyBenefit,
  claimAge,
  deathAge = 85
) => {
  const months = Math.max(0, (deathAge - claimAge) * MONTHS);
  return Math.round(monthlyBenefit * months);
};

/**
 * Calculate monthly decrease due to earnings test rules
 */
export function compareFRAandChosenAge({
  estimatedEarnings = 0,
  benefitStartDate = null,
  birthDate = null,
}) {
  const LIMIT_UNDER_FRA = 23400; // 2025 limit
  const LIMIT_SAME_YEAR = 62160; // 2025 limit

  if (!birthDate || !benefitStartDate) {
    return {
      monthlyAtFRA: 0,
      monthlyAtClaim: 0,
      benefitStartYear: null,
      fraYear: null,
    };
  }
  const birthYear = moment(birthDate).year();
  const fraAge = getFRA(birthYear);
  const fraYear = birthYear + fraAge;
  const benefitStartYear = moment(benefitStartDate).year();
  const monthOfFRABirthday = moment(birthDate).month() + 1; // 1-12
  const monthsBeforeFRA = monthOfFRABirthday - 1;

  let monthlyAtFRA = 0;
  let monthlyAtClaim = 0;

  if (benefitStartYear < fraYear) {
    // Standard rule: before FRA year
    const amountOver = Math.max(0, estimatedEarnings - LIMIT_UNDER_FRA);
    const totalReduction = amountOver / 2;
    monthlyAtFRA = totalReduction / 12;
  } else if (benefitStartYear === fraYear) {
    // Special rule: same year as FRA
    const amountOver = Math.max(0, estimatedEarnings - LIMIT_SAME_YEAR);
    const totalReduction = amountOver / 3;
    monthlyAtClaim = monthsBeforeFRA > 0 ? totalReduction / monthsBeforeFRA : 0;
  }

  return {
    monthlyAtFRA: Math.round(monthlyAtFRA * 100) / 100,
    monthlyAtClaim: Math.round(monthlyAtClaim * 100) / 100,
    benefitStartYear,
    fraYear,
  };
}

/**
 * Calculate monthly decrease due to earnings test rules
 */
// export function compareFRAandChosenAge({
//   estimatedEarnings = 0,
//   benefitStartDate = null,
//   birthDate = null,
//   pia = 0, // Add PIA parameter
//   military = false, // Add military parameter
// }) {
//   const LIMIT_UNDER_FRA = 23400; // 2025 limit
//   const LIMIT_SAME_YEAR = 62160; // 2025 limit

//   if (!birthDate || !benefitStartDate) {
//     return {
//       monthlyAtFRA: 0,
//       monthlyAtClaim: 0,
//       benefitStartYear: null,
//       fraYear: null,
//     };
//   }

//   const birthYear = moment(birthDate).year();
//   const fraAge = getFRA(birthYear);
//   const fraYear = birthYear + fraAge;
//   const benefitStartYear = moment(benefitStartDate).year();
//   const monthOfFRABirthday = moment(birthDate).month() + 1; // 1-12
//   const monthsBeforeFRA = monthOfFRABirthday - 1;

//   // Adjust PIA for military service (5% increase if military is true)
//   const adjustedPia = military ? pia * 1.05 : pia;

//   let monthlyAtFRA = 0;
//   let monthlyAtClaim = 0;

//   if (benefitStartYear < fraYear) {
//     // Standard rule: before FRA year
//     const amountOver = Math.max(0, estimatedEarnings - LIMIT_UNDER_FRA);
//     const totalReduction = amountOver / 2;
//     monthlyAtFRA =
//       adjustedPia > 0
//         ? Math.min(totalReduction / 12, adjustedPia)
//         : totalReduction / 12;
//   } else if (benefitStartYear === fraYear) {
//     // Special rule: same year as FRA
//     const amountOver = Math.max(0, estimatedEarnings - LIMIT_SAME_YEAR);
//     const totalReduction = amountOver / 3;
//     monthlyAtClaim =
//       adjustedPia > 0 && monthsBeforeFRA > 0
//         ? Math.min(totalReduction / monthsBeforeFRA, adjustedPia)
//         : 0;
//   }

//   return {
//     monthlyAtFRA: Math.round(monthlyAtFRA * 100) / 100,
//     monthlyAtClaim: Math.round(monthlyAtClaim * 100) / 100,
//     benefitStartYear,
//     fraYear,
//   };
// }

/**
 * Calculate breakeven, lifetime totals, and all outputs (core analysis)
 */
export function calculateRetirementAnalysis({
  pia = 2000,
  birthDate = null,
  firstClaimAge = 64,
  secondClaimAge = 69,
  lifeExpectancy = 90,
  military = false,
}) {
  if (!birthDate) {
    return;
  }

  const birthYear = moment(birthDate).year();
  const fraAge = getFRA(birthYear);

  // Adjust PIA for military service (e.g., 5% increase if military is true)
  const adjustedPia = military ? pia * 1.05 : pia; // 5% increase for military service

  // 1️⃣ Calculate monthly benefits
  const earlyMonthly = calculateMonthlyBenefitFromPIA(
    adjustedPia,
    firstClaimAge,
    fraAge
  );
  const laterMonthly = calculateMonthlyBenefitFromPIA(
    adjustedPia,
    secondClaimAge,
    fraAge
  );

  // 2️⃣ Calculate lifetime benefits
  const earlyLifetime = calculateLifetimeBenefit(
    earlyMonthly,
    firstClaimAge,
    lifeExpectancy
  );
  const laterLifetime = calculateLifetimeBenefit(
    laterMonthly,
    secondClaimAge,
    lifeExpectancy
  );

  // 3️⃣ Breakeven calculation
  const monthsBetween = (secondClaimAge - firstClaimAge) * MONTHS;
  const headStart = earlyMonthly * monthsBetween;
  const catchUp = laterMonthly - earlyMonthly;
  const monthsToBreakeven = catchUp > 0 ? headStart / catchUp : null;

  let breakYears = null;
  let breakMonths = null;
  let formatted = "No breakeven";

  if (monthsToBreakeven && monthsToBreakeven !== Infinity) {
    const totalMonths = secondClaimAge * MONTHS + monthsToBreakeven;
    breakYears = Math.floor(totalMonths / MONTHS);
    breakMonths = Math.round(totalMonths % MONTHS);
    formatted = `${breakYears} years and ${breakMonths} months`;
  }

  const breakEvenAge = {
    years: breakYears,
    months: breakMonths,
    decimal: breakYears !== null ? breakYears + breakMonths / 12 : null,
    formatted,
  };

  return {
    fraAge,
    firstClaimAge,
    secondClaimAge,
    pia: adjustedPia,
    earlyMonthly,
    laterMonthly,
    earlyLifetime,
    laterLifetime,
    breakEvenAge,
    lifeExpectancy,
    summary: {
      "Monthly Benefit (Early Age)": `$${earlyMonthly.toFixed(2)}`,
      "Monthly Benefit (Later Age)": `$${laterMonthly.toFixed(2)}`,
      "Lifetime Benefit (Early Age)": `$${earlyLifetime.toLocaleString()}`,
      "Lifetime Benefit (Later Age)": `$${laterLifetime.toLocaleString()}`,
      "Breakeven Age": breakEvenAge.formatted,
      "Life Expectancy Used": lifeExpectancy,
    },
  };
}

/**
 * Main line chart generator for Social Security breakdown analysis.
 * Compares "If I take Social Security at First Claim Age" vs "If I take at Second Claim Age".
 */
export function generateLineChartData({
  fraBenefit = 0,
  birthYear = null,
  firstClaimAge = 64,
  secondClaimAge = 69,
  lifeExpectancy = 90,
  military = false,
}) {
  if (!birthYear) {
    return { labels: [], datasets: [] }; // Return empty chart if birthYear is missing
  }

  const fraAge = getFRA(birthYear);
  const adjustedFraBenefit = military ? fraBenefit * 1.05 : fraBenefit; // Adjust PIA for military

  // Compute monthly amounts using SSA rules
  const firstMonthly = calculateMonthlyBenefitFromPIA(
    adjustedFraBenefit,
    firstClaimAge,
    fraAge
  );
  const secondMonthly = calculateMonthlyBenefitFromPIA(
    adjustedFraBenefit,
    secondClaimAge,
    fraAge
  );

  // Labels = ages from earliest claim age through life expectancy
  const startAge = Math.min(firstClaimAge, secondClaimAge);
  const labels = [];
  const firstData = []; // Total cumulative benefit if claimed at firstClaimAge
  const secondData = []; // Total cumulative benefit if claimed at secondClaimAge

  // Running totals
  let firstTotal = 0;
  let secondTotal = 0;

  for (let age = startAge; age <= lifeExpectancy; age++) {
    labels.push(`Age ${age}`);

    // First claim age: starts receiving at firstClaimAge
    if (age >= firstClaimAge) {
      firstTotal += firstMonthly * 12;
    }
    // Second claim age: starts receiving at secondClaimAge
    if (age >= secondClaimAge) {
      secondTotal += secondMonthly * 12;
    }

    firstData.push(parseFloat(firstTotal.toFixed(2)));
    secondData.push(parseFloat(secondTotal.toFixed(2)));
  }

  // Prepare dataset structure for chart.js or recharts
  const lineChartData = {
    labels,
    datasets: [
      {
        label: `If I Take Social Security at Age ${firstClaimAge}`,
        data: firstData,
        borderColor: "#C9D5F1",
        backgroundColor: "#C9D5F1",
        tension: 0.3,
        fill: true,
      },
      {
        label: `If I Take Social Security at Age ${secondClaimAge}`,
        data: secondData,
        borderColor: "#2A3DA3",
        backgroundColor: "#2A3DA3",
        tension: 0.3,
        fill: true,
      },
    ],
  };

  return lineChartData;
}
